// Typed fetch client for the platform-graph service (customer surface).
//
// Type-safety chain (ADR-014 §4; proven in operator-console/src/api/
// operator-graph.ts): services/platform-graph route `schema:` (Zod) →
// @fastify/swagger openapi.yaml (3.1) → openapi-typescript
// src/api/schema.generated.ts (`paths`) → openapi-fetch typed client
// (this file) → TanStack Query hooks. The hand-typed `Paths` placeholder
// that lived here in Phase 0 is gone — the generated `paths` is the
// source of truth, drift-guarded by src/api/schema.generated.test.ts.
//
// Adding a new /v1/* endpoint:
//   1. Backend route declares `schema: { ... }`.
//   2. pnpm --filter @novemai/platform-graph openapi:dump
//   3. pnpm --filter @novemai/customer-web openapi:types
//   4. Drift tests in BOTH workspaces fail in CI if either step is skipped.
//   5. Hooks call `platformGraph.GET("/v1/<endpoint>")` with autocompleted
//      path + typed response, unwrapped via `unwrapEnvelope`.

import createClient, { type Middleware } from "openapi-fetch";

import { resolveCustomerPlatformGraphBaseUrl } from "@/config/customer-deployment";

import { unwrapEnvelope } from "./errors";
import type { paths } from "./schema.generated";

// Resolve at module initialization, before openapi-fetch can construct a
// request and before the bearer middleware below can attach a customer token.
const baseUrl = resolveCustomerPlatformGraphBaseUrl({
  configuredUrl: import.meta.env["VITE_PLATFORM_GRAPH_BASE_URL"],
  mode: import.meta.env.PROD ? "production" : "development",
});

// ---------------------------------------------------------------------------
// Bearer-token injector (B0 deliverable 5)
// ---------------------------------------------------------------------------
//
// The customers-realm access token is owned by react-oidc-context, which
// only exposes it through the `useAuth()` hook. A module-level holder lets
// the openapi-fetch middleware attach `Authorization: Bearer <token>` to
// every request without threading the hook through every call site. The
// `useAuthFetch` hook (src/auth/useAuthFetch.ts) keeps this holder in sync
// with the OIDC user lifecycle. Tokens live only in memory here (the OIDC
// store is sessionStorage per ADR-007) — never logged, never persisted.

let currentAccessToken: string | null = null;

/** Set/clear the customers-realm Bearer token used by the API client. */
export function setAccessToken(token: string | null): void {
  currentAccessToken = token;
}

/**
 * Read the customers-realm Bearer token currently bound to the API client.
 * The openapi-fetch middleware attaches it to every JSON call automatically;
 * this getter exists only for the rare NON-JSON download paths (e.g. the
 * calendar `.ics` export) that bypass openapi-fetch and issue a raw `fetch`
 * needing the same Authorization header. Returns `null` when no session is
 * bound. Tokens stay in memory only (ADR-007) — callers must never log/persist.
 */
export function getAccessToken(): string | null {
  return currentAccessToken;
}

/** Base URL the API client targets — reused by raw-fetch download paths. */
export const platformGraphBaseUrl = baseUrl;

const bearerMiddleware: Middleware = {
  onRequest({ request }) {
    if (currentAccessToken) {
      request.headers.set("Authorization", `Bearer ${currentAccessToken}`);
    }
    return request;
  },
};

// ---------------------------------------------------------------------------
// Session-expiry recovery (SEM-6327)
// ---------------------------------------------------------------------------
//
// Measured on the live demo 2026-08-06: a tab whose silent-renew timer gets
// throttled (backgrounded, machine asleep) never renews, and since the
// customers realm mints refresh tokens with the SAME `exp` as the access token
// there is no window left to recover in. That tab then keeps rendering a fully
// authenticated shell — menus, roles and identity all come from claims already
// in memory — while every request 401s. react-oidc-context never notices:
// RequireAuth reacts to `isAuthenticated`, which only flips on a UserManager
// event, and the event that would have fired is the timer that never ran.
//
// So the rejected REQUEST is the only expiry signal that reliably arrives.
// The app root registers a recovery routine here (silent renew, else
// re-login) and the middleware below triggers it.

type AuthExpiredHandler = () => Promise<void>;

let authExpiredHandler: AuthExpiredHandler | null = null;
let recovery: { token: string; promise: Promise<void> } | null = null;

/**
 * Register the routine that recovers an expired session. Set once near the
 * app root by `useAuthFetch`, which owns the OIDC lifecycle; pass `null` to
 * clear it on unmount.
 */
export function setAuthExpiredHandler(handler: AuthExpiredHandler | null): void {
  authExpiredHandler = handler;
  recovery = null;
}

const sessionExpiryMiddleware: Middleware = {
  async onResponse({ request, response, options }) {
    const rejectedBearer = request.headers.get("Authorization");
    if (response.status !== 401 || !rejectedBearer || currentAccessToken === null) {
      return response;
    }

    // A late response for the old token must not renew an already recovered
    // session again. Parallel rejections for the current token share one renewal.
    if (rejectedBearer === `Bearer ${currentAccessToken}` && authExpiredHandler) {
      if (recovery?.token !== currentAccessToken) {
        const pending = {
          token: currentAccessToken,
          promise: Promise.resolve().then(authExpiredHandler).catch(() => {}),
        };
        recovery = pending;
        void pending.promise.finally(() => {
          if (recovery === pending) recovery = null;
        });
      }
      await recovery.promise;
    }

    // Retry reads inside their original request, before Query observes a 401.
    // Invalidating queries here races both the token render and the query's
    // still-running promise. Writes are NEVER replayed automatically.
    if (
      (request.method === "GET" || request.method === "HEAD") &&
      currentAccessToken !== null &&
      rejectedBearer !== `Bearer ${currentAccessToken}`
    ) {
      request.signal.throwIfAborted();
      const headers = new Headers(request.headers);
      headers.set("Authorization", `Bearer ${currentAccessToken}`);
      // Use this request's transport, and bypass middleware for this single
      // retry: even a second 401 cannot start an unbounded recovery loop.
      return options.fetch(new Request(request, { headers }));
    }

    return response;
  },
};

export const platformGraph = createClient<paths>({
  baseUrl,
  // OIDC cookies + W3C traceparent forwarding (ADR-010). The credentials
  // flag is the cross-origin cookie precondition; the Bearer header carries
  // the customers-realm access token for the verifier.
  credentials: "include",
});

platformGraph.use(bearerMiddleware);
platformGraph.use(sessionExpiryMiddleware);

/**
 * Healthz round-trip helper used by the home page query. Returns the typed
 * `data` payload via the canonical envelope unwrap helper (src/api/errors.ts);
 * throws `EnvelopeError` on any failure or malformed response — TanStack
 * Query's error state surfaces it to the toast adapter.
 */
export async function fetchHealthz(): Promise<
  NonNullable<
    paths["/v1/healthz"]["get"]["responses"][200]["content"]["application/json"]["data"]
  >
> {
  return unwrapEnvelope(platformGraph.GET("/v1/healthz"));
}
